import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  Portal,
} from '@mui/material';
import { PhotoCamera, Visibility, Delete } from '@mui/icons-material';
import useManageOrder from './useManageOrder';
import { toast } from 'react-toastify';
import ImageUploadModal from '../../utils/ImageUploadModal';
import { formatDate } from '../../utils/dateUtils';
import { showsBillUrl, showsTransactionUrl } from '../../utils/websiteLinkFields';
import ShippingTypeDialog from '../../sharedComponents/ShippingTypeDialog';
import ConfirmDialog from '../../sharedComponents/ConfirmDialog';
import OrderByOptionService from '../setting/orderByOption/OrderByOptionService';
import GetCustomerProfileService from '../../services/GetCustomerProfileService';
import OrderByMercariNateDailyUsageLabel from '../../sharedComponents/OrderByMercariNateDailyUsageLabel';
import { useSelector } from 'react-redux';
import useRoutingSuggestion from '../../hooks/useRoutingSuggestion';
import { extractItemNoFromUrl } from '../../utils/postcodeExtractor';
import OrdererConfirmModal from '../../sharedComponents/OrdererConfirmModal';
import OrderService from './OrderService';
import MercariService from '../mercari/mercariService';
import AuctionOrderModal from '../mercari/AuctionOrderModal';

const PendingBeamBeforePurchases = () => {
  const shopName = useSelector((state) => state.shop.name);
  const { 
    orders, 
    isLoading, 
    error, 
    getPendingBeamBeforePurchases, 
    updateOrderImageAndConfirm,
    cancelPendingBeamBeforePurchases
  } = useManageOrder();
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [isHandCarry, setIsHandCarry] = useState(false);
  const [showShippingTypeDialog, setShowShippingTypeDialog] = useState(false);
  const [customerHasEverHadAirOrder, setCustomerHasEverHadAirOrder] = useState(false);
  const [billUrl, setBillUrl] = useState('');
  const [transactionPageUrl, setTransactionPageUrl] = useState('');
  const [isSeven, setIsSeven] = useState(false);
  const [sevenFee, setSevenFee] = useState('');
  const [orderByOptionId, setOrderByOptionId] = useState(null);
  const [orderByOptions, setOrderByOptions] = useState([]);
  const [smileNameInput, setSmileNameInput] = useState('');
  const [imageWarning, setImageWarning] = useState(null); // Postcode / name mismatch after submit
  const [wrongImageWarning, setWrongImageWarning] = useState(null); // Wrong item — contact Gen to fix
  const [pendingOrdererMatch, setPendingOrdererMatch] = useState(null);
  const [isCheckingOrderer, setIsCheckingOrderer] = useState(false);
  const imageValidationPromiseRef = useRef(null); // OCR promise started when image is selected
  const imageValidationResultRef = useRef(null);
  const skipSmileNameOcrCheckRef = useRef(false);
  const { routingSuggestion, fetchRoutingSuggestion, clearRoutingSuggestion } = useRoutingSuggestion();
  const suggestedOrderByOptionId = routingSuggestion?.orderer?.orderByOption?.id;

  // Won-auction cases share this table: same claim behavior, and their View
  // Details opens the order-creation modal (the order doesn't exist yet).
  const [auctionItems, setAuctionItems] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [auctionTakeover, setAuctionTakeover] = useState(null);
  const [auctionToCancel, setAuctionToCancel] = useState(null);

  const fetchAuctionItems = async () => {
    if (!shopName) return;
    try {
      const res = await MercariService.getAuctionAttentionItems(shopName);
      setAuctionItems(res.data?.items || []);
    } catch (err) {
      console.error('fetch auction attention error:', err);
    }
  };

  useEffect(() => {
    getPendingBeamBeforePurchases();
  }, []);

  useEffect(() => {
    fetchAuctionItems();
  }, [shopName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live refresh when another admin claims/releases a case (or a new case appears)
  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, {
      withCredentials: true,
    });
    socket.on('case_claim_updated', ({ caseType } = {}) => {
      if (caseType === 'pending_beam') getPendingBeamBeforePurchases();
      if (caseType === 'auction_won') fetchAuctionItems();
    });
    socket.on('orders_updated', () => getPendingBeamBeforePurchases());
    return () => { socket.disconnect(); };
  }, []);

  // Force-close: if the case I have open gets taken over by another admin, the
  // refreshed list shows it as claimed-but-not-mine — close my dialog. Driven by
  // the server-computed claimedByMe (no client-side id comparison).
  useEffect(() => {
    if (!selectedOrder) return;
    const fresh = (orders || []).find((o) => o.orderId === selectedOrder.orderId);
    if (fresh && fresh.claimedByName && !fresh.claimedByMe) {
      setShowShippingTypeDialog(false);
      setOpenDialog(false);
      setSelectedOrder(null);
      toast.info(`เคสนี้ถูก ${fresh.claimedByName} รับช่วงต่อแล้ว`);
    }
  }, [orders]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    OrderByOptionService.getActiveOrderByOptions()
      .then(res => setOrderByOptions(res.data || []))
      .catch(err => console.error('Failed to fetch order-by options:', err));
  }, []);

  const handleImageUrlChange = (event) => {
    setImageUrl(event.target.value);
  };

  const callRoutingSuggestion = async (order, isAirOverride) => {
    // Fetch suggestion for display/audit only — admin selects orderBy manually
    await fetchRoutingSuggestion({
      website: order?.website || null,
      amount: order?.amount,
      isAir: isAirOverride ?? order?.isHandCarry ?? false,
      customerId: order?.customerId || null,
      paymentMethod: order?.sevenFee != null ? 'sevenEleven' : 'any',
    });
  };

  // Auction rows: claim first (same semantics as beam), then open the
  // order-creation modal. 409 → ask before takeover.
  const openAuctionCase = async (item, takeover = false) => {
    try {
      await MercariService.claimAuctionCase({ id: item.id, takeover, shop: shopName });
      setSelectedAuction(item);
    } catch (err) {
      if (err.response?.status === 409) setAuctionTakeover(item);
      else toast.error('เกิดข้อผิดพลาด');
    }
  };

  const closeAuctionModal = () => {
    if (selectedAuction) MercariService.releaseAuctionCase(selectedAuction.id).catch(() => {});
    setSelectedAuction(null);
  };

  const handleAuctionCreated = () => {
    setSelectedAuction(null);
    fetchAuctionItems();
    getPendingBeamBeforePurchases();
  };

  // Delete a won-auction case that can never be purchased (e.g. seller banned).
  // Soft-cancels on the backend (kept with who-cancelled for the audit) so it
  // stops notifying. Confirm first — auctionToCancel drives the dialog.
  const handleConfirmCancelAuction = async () => {
    if (!auctionToCancel) return;
    try {
      const res = await MercariService.cancelAuctionItem(auctionToCancel.id);
      if (res.data?.success) {
        toast.success('ลบรายการประมูลแล้ว');
        setAuctionToCancel(null);
        fetchAuctionItems();
      } else {
        toast.error('เกิดข้อผิดพลาดในการลบรายการประมูล');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบรายการประมูล');
    }
  };

  // If another admin already holds the case, confirm before taking it over;
  // otherwise open straight away. takeoverOrder drives the confirm dialog.
  const [takeoverOrder, setTakeoverOrder] = useState(null);
  const handleOpenDialog = (order) => {
    // Held by another admin (server-computed) → confirm before taking over.
    if (order.claimedByName && !order.claimedByMe) {
      setTakeoverOrder(order);
      return;
    }
    openDialogNow(order, false);
  };

  const openDialogNow = async (order, takeover = false) => {
    setSelectedOrder(order);
    // Take the case — other admins see "taken by {me}" (fire-and-forget)
    OrderService.claimPendingBeam(order.orderId, takeover).catch(() => {});
    // Initialize isHandCarry, orderByOptionId, billUrl from order data
    setIsHandCarry(order?.isHandCarry || false);
    setOrderByOptionId(order?.orderByOptionId ?? null);
    setImageUrl('');
    setBillUrl(order?.billUrl || '');
    setTransactionPageUrl(order?.transactionPageUrl || '');
    const existingSevenFee = order?.sevenFee != null ? String(order.sevenFee) : '';
    setSevenFee(existingSevenFee);
    setIsSeven(existingSevenFee !== '');
    setSmileNameInput(order?.smileName || '');
    setSelectedImages([]);
    setCustomerHasEverHadAirOrder(false);
    clearRoutingSuggestion();
    skipSmileNameOcrCheckRef.current = order?.isNoTracking === true;

    // Ship/Air reminder: ask on EVERY case that isn't already hand-carry, so the
    // admin consciously confirms with the customer (or the chat) before keying.
    // The old "only when isHandCarry never set" check was dead code — the list
    // endpoint always sends a hard boolean (!!order.handCarry), never null.
    const hasContact = order?.contact?.trim();

    if (hasContact && !order?.isHandCarry) {
      try {
        const response = await GetCustomerProfileService.getCustomerProfileFromContact({
          contact: order.contact,
          shop: shopName,
        });
        setCustomerHasEverHadAirOrder(!!response?.data?.hasEverHadAirOrder);
      } catch {
        // Ignore; dialog will show without the notice
      }
      // Stacked like the auction modal: details dialog opens underneath, the
      // Ship/Air reminder sits on top. Choosing refetches routing with the pick.
      setOpenDialog(true);
      callRoutingSuggestion(order, order?.isHandCarry || false);
      setShowShippingTypeDialog(true);
    } else {
      setOpenDialog(true);
      callRoutingSuggestion(order, order?.isHandCarry || false);
    }
  };

  const handleCloseDialog = () => {
    // Release the case so it returns to the unclaimed pool (others can take it)
    if (selectedOrder?.orderId) {
      OrderService.releasePendingBeam(selectedOrder.orderId).catch(() => {});
    }
    setOpenDialog(false);
    setSelectedOrder(null);
    setImageUrl('');
    setBillUrl('');
    setTransactionPageUrl('');
    setIsSeven(false);
    setSevenFee('');
    setSelectedImages([]);
    setIsHandCarry(false);
    setOrderByOptionId(null);
    setSmileNameInput('');
    setShowShippingTypeDialog(false);
    setCustomerHasEverHadAirOrder(false);
    clearRoutingSuggestion();
    imageValidationPromiseRef.current = null;
    imageValidationResultRef.current = null;
  };

  const handleOpenImageModal = () => {
    setOpenImageModal(true);
  };

  const handleCloseWrongImageDialog = () => {
    if (wrongImageWarning?.wrongImagePreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(wrongImageWarning.wrongImagePreviewUrl);
    }
    setWrongImageWarning(null);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleImageConfirm = (images) => {
    setSelectedImages(images);
    setOpenImageModal(false);

    // Kick off OCR validation in background for Mercari orders
    const isMercari = selectedOrder?.website === 'Mercari';
    if (isMercari && images.length === 1) {
      imageValidationResultRef.current = null;
      const expectedName   = selectedOrder?.smileNameFull || null;
      const warehouse      = routingSuggestion?.warehouse ?? null;
      const expectedItemNo = extractItemNoFromUrl(selectedOrder?.url);
      imageValidationPromiseRef.current = import('../../utils/postcodeExtractor')
        .then(({ validatePurchaseConfirmImage }) =>
          validatePurchaseConfirmImage(
            images[0],
            isHandCarry,
            expectedName,
            warehouse,
            expectedItemNo,
            { skipSmileNameOcrCheck: skipSmileNameOcrCheckRef.current },
          )
        )
        .then((result) => {
          imageValidationResultRef.current = result;
          return result;
        })
        .catch((err) => {
          console.error('PendingBeam OCR validation error (non-blocking):', err);
          imageValidationResultRef.current = { isValid: false, cannotValidate: true, error: err.message };
          return imageValidationResultRef.current;
        });
    } else {
      imageValidationPromiseRef.current = null;
      imageValidationResultRef.current = null;
    }
  };

  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
    setOrderToCancel(null);
  };

  const handleConfirmCancel = async () => {
    if (!orderToCancel) return;

    try {
      const result = await cancelPendingBeamBeforePurchases(orderToCancel.orderId);

      if (result) {
        toast.success("ส่งเข้ารายการยกเลิกแล้ว — สร้างรายการคืนเงินที่แท็บโอนเงินคืน");
        handleCloseCancelDialog();
        // Refresh the data
        getPendingBeamBeforePurchases();
      } else {
        toast.error("เกิดข้อผิดพลาดในการยกเลิกคำสั่งซื้อ");
      }
    } catch (error) {
      console.error('Error canceling order:', error);
      toast.error("เกิดข้อผิดพลาดในการยกเลิกคำสั่งซื้อ");
    }
  };

  const handleShippingTypeSelect = (type) => {
    const isAir = type === 'Air';
    setIsHandCarry(isAir);
    setShowShippingTypeDialog(false);
    setOpenDialog(true);
    if (selectedOrder) {
      callRoutingSuggestion(selectedOrder, isAir);
    }
  };

  const handleShippingTypeCancel = () => {
    // Backed out before processing — release the claim
    if (selectedOrder?.orderId) {
      OrderService.releasePendingBeam(selectedOrder.orderId).catch(() => {});
    }
    setShowShippingTypeDialog(false);
    setOpenDialog(false); // details dialog is stacked underneath now
    setSelectedOrder(null);
    setIsHandCarry(false);
    setOrderByOptionId(null);
    setBillUrl('');
    setTransactionPageUrl('');
    setIsSeven(false);
    setSevenFee('');
    setCustomerHasEverHadAirOrder(false);
  };

  const handleSubmit = async () => {
    // Validate that exactly 1 file is attached from handleOpenImageModal (required)
    if (selectedImages.length !== 1) {
      toast.warning("กรุณาอัพโหลดรูปยืนยันการสั่งซื้อ 1 ไฟล์");
      return;
    }

    if (!orderByOptionId) {
      toast.warning('กรุณาเลือก Order By ก่อนบันทึก');
      return;
    }

    try {
      const formData = new FormData();
      
      // Add orderId
      formData.append('orderId', selectedOrder.orderId);
      
      // Add images from ImageUploadModal (should be exactly 1 file)
      selectedImages.forEach((image, index) => {
        formData.append('images', image);
      });
      
      // Add imageUrl if provided
      if (imageUrl.trim()) {
        formData.append('imageUrl', imageUrl);
      }

      // Add isHandCarry (air item types assigned later via hand-carry admin page)
      formData.append('isHandCarry', isHandCarry);

      formData.append('orderByOptionId', orderByOptionId);

      // Add billUrl if provided (Rakuten only — same gate as OrderForm)
      if (showsBillUrl(selectedOrder?.website) && billUrl.trim()) {
        formData.append('billUrl', billUrl.trim());
      }

      // Add transactionPageUrl if provided (all websites except Mercari/Yahoo Auction/PayPay)
      if (showsTransactionUrl(selectedOrder?.website) && transactionPageUrl.trim()) {
        formData.append('transactionPageUrl', transactionPageUrl.trim());
      }

      // Add sevenFee if provided
      if (sevenFee.trim()) {
        formData.append('sevenFee', sevenFee.trim());
      }

      // Add smileNameInput (editable smile name)
      formData.append('smileNameInput', smileNameInput);

      // Routing tracking: what system suggested vs what admin chose
      const suggestedOrdererId = routingSuggestion?.orderer?.orderByOption?.id;
      const suggestedWarehouseId = routingSuggestion?.warehouse?.id;
      if (suggestedOrdererId) {
        formData.append('suggestedOrderByOptionId', suggestedOrdererId);
      }
      if (suggestedWarehouseId) {
        formData.append('suggestedWarehouseReceivingAddressId', suggestedWarehouseId);
      }

      // Await OCR validation if it's running (non-blocking: max 10s timeout)
      let validationResult = imageValidationResultRef.current;
      if (!validationResult && imageValidationPromiseRef.current) {
        try {
          validationResult = await Promise.race([
            imageValidationPromiseRef.current,
            new Promise((resolve) =>
              setTimeout(() => resolve({ isValid: false, cannotValidate: true, error: 'timeout' }), 10000)
            ),
          ]);
        } catch {
          validationResult = null;
        }
      }

      setIsCheckingOrderer(true);
      const result = await updateOrderImageAndConfirm(formData);
      setIsCheckingOrderer(false);

      if (result) {
        if (result?.ordererMatch) {
          setPendingOrdererMatch(result.ordererMatch);
        }
        toast.success("อัพเดทรูปภาพเรียบร้อย");
        handleCloseDialog();
        getPendingBeamBeforePurchases();

        const hasItemNoMismatch  = validationResult?.itemNoMismatch === true;
        const hasPostcodeMismatch = validationResult && !validationResult.isValid && !validationResult.cannotValidate;
        const hasNameMismatch    = validationResult?.nameMismatchMessage;

        if (hasItemNoMismatch) {
          const wrongImagePreviewUrl =
            selectedImages[0] ? URL.createObjectURL(selectedImages[0]) : null;
          setTimeout(() =>
            setWrongImageWarning({
              ...validationResult,
              expectedUrl: selectedOrder.url,
              wrongImagePreviewUrl,
            }),
            100,
          );
        } else if (hasPostcodeMismatch || hasNameMismatch) {
          setTimeout(() => setImageWarning(validationResult), 100);
        }
      } else {
        toast.error("เกิดข้อผิดพลาดในการอัพเดทรูปภาพ");
      }
    } catch (error) {
      console.error('Error updating order image:', error);
      toast.error("เกิดข้อผิดพลาดในการอัพเดทรูปภาพ");
    } finally {
      setIsCheckingOrderer(false);
    }
  };

  // Only show the full-page spinner on the initial load. Background refreshes
  // (socket claim/release, post-submit refetch) update the table in place so the
  // page doesn't blink/reload on every claim or release.
  // Mark a case as "waiting for an external orderer" — only needs Order By.
  // Releases the claim, drops it from the notify, and the table shows "waiting since".
  const handleMarkWaiting = async () => {
    if (!orderByOptionId) {
      toast.warning('กรุณาเลือก Order By ก่อน');
      return;
    }
    if (!selectedOrder?.orderId) return;
    try {
      setIsCheckingOrderer(true);
      const res = await OrderService.markPendingBeamWaiting(selectedOrder.orderId, orderByOptionId);
      if (res?.data) {
        toast.success('บันทึกสถานะรอออเดอร์เรียบร้อย');
        handleCloseDialog();
        getPendingBeamBeforePurchases();
      } else {
        toast.error('เกิดข้อผิดพลาด');
      }
    } catch (error) {
      console.error('Error marking waiting for order:', error);
      toast.error('เกิดข้อผิดพลาด');
    } finally {
      setIsCheckingOrderer(false);
    }
  };

  if (isLoading && !orders?.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        รายการรอสั่งซื้อ
        <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          (Pending Purchases)
        </Typography>
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>No.</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auctionItems.map((item, idx) => (
              <TableRow key={`auction-${item.id}`} sx={{ backgroundColor: '#fef2f2' }}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>
                  {item.finishTime ? formatDate(item.finishTime) : ''}
                  {item.winnerPeriodEndTime && new Date(item.winnerPeriodEndTime) < new Date() && (
                    <Chip label="เลยกำหนด!" color="error" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>{item.customerContact || ''}</TableCell>
                <TableCell>
                  <a
                    href={item.itemUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all', color: '#1976d2', textDecoration: 'none' }}
                  >
                    {item.itemUrl}
                  </a>
                </TableCell>
                <TableCell>
                  <Chip label={`🔨 ประมูลชนะ (${item.orderByLabel || '-'})`} color="error" size="small" sx={{ mr: 0.5 }} />
                  {item.claimedByName && (
                    item.claimedByMe
                      ? <Chip label="คุณกำลังดำเนินการ" color="success" size="small" />
                      : <Chip label={`กำลังดำเนินการโดย ${item.claimedByName}`} color="warning" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Visibility />}
                      onClick={() => openAuctionCase(item, false)}
                      size="small"
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => setAuctionToCancel(item)}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {orders && orders.length > 0 ? (
              orders.map((order, index) => (
                <TableRow key={order.orderId || index}>
                  <TableCell>{auctionItems.length + index + 1}</TableCell>
                  <TableCell>{formatDate(order.orderDate) || ''}</TableCell>
                  <TableCell>{order.contact || ''}</TableCell>
                  <TableCell>
                    {order.url ? (
                      <a 
                        href={order.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ wordBreak: 'break-all', color: '#1976d2', textDecoration: 'none' }}
                      >
                        {order.url}
                      </a>
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {order.waitingForOrderAt ? (
                      <Chip
                        label={`รอออเดอร์ตั้งแต่ ${formatDate(order.waitingForOrderAt, true)}`}
                        color="info"
                        size="small"
                      />
                    ) : order.claimedByName ? (
                      order.claimedByMe ? (
                        <Chip label="คุณกำลังดำเนินการ" color="success" size="small" />
                      ) : (
                        <Chip label={`กำลังดำเนินการโดย ${order.claimedByName}`} color="warning" size="small" />
                      )
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Visibility />}
                        onClick={() => handleOpenDialog(order)}
                        size="small"
                      >
                        View Details
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleCancelOrder(order)}
                        size="small"
                      >
                        Cancel
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Order Details Modal */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Order Details - #{selectedOrder?.orderId}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ marginTop: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Order ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.orderId || ''}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.orderDate
                      ? new Date(selectedOrder.orderDate).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
                      : ''}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Smile Name"
                    value={smileNameInput}
                    onChange={(e) => setSmileNameInput(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Smile Name (JP)
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.smileNameJp || ''}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Contact
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.contact || ''}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Quantity
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.qty || ''}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.amount || ''}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    ค่าส่งในญี่ปุ่น
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedOrder?.deliveryFeeJp != null ? `¥${selectedOrder.deliveryFeeJp} ${selectedOrder?.isCod ? ' (ปลายทาง)' : ''}` : '-'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Product URL
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <a 
                      href={selectedOrder?.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-all', color: '#1976d2' }}
                    >
                      {selectedOrder?.url || ''}
                    </a>
                  </Typography>
                </Grid>
              </Grid>

              {routingSuggestion && routingSuggestion.suggestionVisible && (
                <Alert
                  severity={routingSuggestion.orderer ? 'info' : 'warning'}
                  sx={{ mt: 2, mb: 1 }}
                >
                  <strong>ระบบแนะนำ:</strong>{' '}
                  {routingSuggestion.orderer ? (
                    <>
                      ผู้สั่ง: <strong>{routingSuggestion.orderer.orderByOption?.label}</strong>
                      {routingSuggestion.suggestedPaymentMethod === 'sevenEleven' && (
                        <span style={{ marginLeft: 6, display: 'inline-block', background: '#1565c0', color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 'bold' }}>จ่าย 7-Eleven</span>
                      )}
                      {routingSuggestion.suggestedPaymentMethod === 'merpay' && (
                        <span style={{ marginLeft: 6, display: 'inline-block', background: '#2e7d32', color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 'bold' }}>จ่าย Merpay</span>
                      )}
                      {routingSuggestion.orderer.remainingOrders != null && ` (เหลือ ${routingSuggestion.orderer.remainingOrders} orders`}
                      {routingSuggestion.orderer.remainingAmountJpy != null && ` / ¥${routingSuggestion.orderer.remainingAmountJpy.toLocaleString()}`}
                      {(routingSuggestion.orderer.remainingOrders != null || routingSuggestion.orderer.remainingAmountJpy != null) && ')'}
                      {routingSuggestion.orderer.isSoftCap && <span style={{ color: 'orange', marginLeft: 4 }}>[soft cap]</span>}
                      {routingSuggestion.orderer.isOverflow && <span style={{ color: 'darkorange', marginLeft: 4, fontWeight: 'bold' }}>[เกินลิมิต — ไม่มีตัวเลือกอื่น]</span>}
                      {routingSuggestion.orderer.sevenElevenRemaining != null && ` | 7-11: ${routingSuggestion.orderer.sevenElevenRemaining}/3`}
                    </>
                  ) : (
                    <span style={{ color: 'red' }}>ไม่มีผู้สั่งที่รองรับได้วันนี้</span>
                  )}
                  {routingSuggestion.warehouse && (
                    <> | โกดัง: <strong>{routingSuggestion.warehouse.addressNickName}</strong></>
                  )}
                  {routingSuggestion.alternatives?.length > 0 && (
                    <> | ตัวเลือกอื่น: {routingSuggestion.alternatives.map(a => a.orderByOption?.label).join(', ')}</>
                  )}
                  {routingSuggestion.urgencyInfo && (() => {
                    const pct = routingSuggestion.urgencyInfo.merpayRemainingPct;
                    const color = pct <= 30 ? '#d32f2f' : pct <= 60 ? '#ed6c02' : '#2e7d32';
                    return (
                      <div style={{ marginTop: 4, fontSize: '0.78rem', color }}>
                        Merpay คงเหลือ {pct}%
                        {' '}(¥{routingSuggestion.urgencyInfo.totalMerpayRemainingJpy?.toLocaleString()})
                        {' '}· เพดานปัจจุบัน ¥{routingSuggestion.urgencyInfo.effectiveMerpayMax?.toLocaleString()}
                      </div>
                    );
                  })()}
                </Alert>
              )}

              <Box sx={{ marginTop: 3, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Shipping Options
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="order-by-label" shrink>Order By</InputLabel>
                    <Select
                      labelId="order-by-label"
                      id="orderByOptionId"
                      value={
                        orderByOptionId != null &&
                        orderByOptions.some((o) => o.id === orderByOptionId)
                          ? orderByOptionId
                          : ''
                      }
                      label="Order By"
                      notched
                      displayEmpty
                      onChange={(e) =>
                        setOrderByOptionId(
                          e.target.value ? parseInt(e.target.value, 10) : null
                        )
                      }
                    >
                      <MenuItem value="" disabled>
                        <em>กรุณาเลือก</em>
                      </MenuItem>
                      {orderByOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                          {suggestedOrderByOptionId === opt.id && (
                            <Typography component="span" variant="caption" color="primary" sx={{ ml: 0.5 }}>✓</Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <OrderByMercariNateDailyUsageLabel
                    orderByOptions={orderByOptions}
                    selectedOrderByOptionId={orderByOptionId}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isHandCarry}
                        onChange={(e) => setIsHandCarry(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="รายการหิ้ว (Hand Carry)"
                  />
                </Box>

                <Typography variant="h6" gutterBottom>
                  Upload Image
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Image URL"
                    value={imageUrl}
                    onChange={handleImageUrlChange}
                    fullWidth
                    placeholder="Enter image URL"
                  />
                  {showsBillUrl(selectedOrder?.website) && (
                    <TextField
                      label="ลิ้งค์บิล (Bill URL)"
                      value={billUrl}
                      onChange={(e) => setBillUrl(e.target.value)}
                      fullWidth
                      placeholder="https://... (optional)"
                    />
                  )}
                  {showsTransactionUrl(selectedOrder?.website) && (
                    <TextField
                      label="Transaction Page"
                      value={transactionPageUrl}
                      onChange={(e) => setTransactionPageUrl(e.target.value)}
                      fullWidth
                      placeholder="https://... transaction page"
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSeven}
                          onChange={(e) => {
                            setIsSeven(e.target.checked);
                            if (!e.target.checked) setSevenFee('');
                          }}
                          color="primary"
                        />
                      }
                      label="จ่าย Seven (Seven-Eleven)"
                    />
                    {isSeven && (
                      <TextField
                        label="Seven Fee (เยน)"
                        value={sevenFee}
                        onChange={(e) => setSevenFee(e.target.value)}
                        size="small"
                        type="number"
                        sx={{ width: 160 }}
                        placeholder="0"
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoCamera />}
                      onClick={handleOpenImageModal}
                    >
                      Upload รูปยืนยันการสั่งซื้อ
                    </Button>
                    
                    {selectedImages.length > 0 && (
                      <Chip 
                        label={selectedImages.length === 1 ? "1 file selected" : `${selectedImages.length} file(s) selected`} 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleMarkWaiting}
            color="warning"
            variant="outlined"
            disabled={!orderByOptionId}
            sx={{ mr: 'auto' }}
          >
            รอออเดอร์ (Waiting for order)
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={openImageModal}
        onRequestClose={handleCloseImageModal}
        onConfirm={handleImageConfirm}
        title="Upload Order Image"
        maxFiles={1}
      />

      {/* Shipping Type Selection Dialog */}
      <ShippingTypeDialog
        open={showShippingTypeDialog}
        onClose={handleShippingTypeCancel}
        onSelect={handleShippingTypeSelect}
        hasEverHadAirOrder={customerHasEverHadAirOrder}
      />

      {/* Cancel Confirmation Dialog */}
      <Dialog open={openCancelDialog} onClose={handleCloseCancelDialog}>
        <DialogTitle>
          ยืนยันการยกเลิกคำสั่งซื้อ
        </DialogTitle>
        <DialogContent>
          <Typography>
            คุณแน่ใจหรือไม่ว่าต้องการยกเลิกคำสั่งซื้อ #{orderToCancel?.orderId}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            ⚠️ ลูกค้าชำระเงินมาก่อนแล้ว — ออเดอร์จะถูกส่งไปที่ "รายการยกเลิก"
            เพื่อสร้างรายการคืนเงิน (รวมหลายออเดอร์ของลูกค้าได้)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>ยกเลิก</Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            ยืนยันการยกเลิก
          </Button>
        </DialogActions>
      </Dialog>

      {/* Wrong Image Dialog — item number mismatch */}
      <Dialog
        open={wrongImageWarning !== null}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#7f1d1d', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: '1.75rem' }}>🚨</span>
            <strong>ติดต่อคุณเกน เพื่อแก้ไข</strong>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ p: 2, mb: 2, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #f59e0b' }}>
            <Typography fontWeight="bold" color="warning.dark">
              รหัสสินค้าในรูปไม่ตรงกับ URL — ติดต่อคุณเกน เพื่อแก้ไข
            </Typography>
          </Box>
          <Box sx={{ p: 2, bgcolor: '#fee2e2', borderRadius: 1, border: '2px solid #b91c1c', mb: 2 }}>
            <Typography fontWeight="bold" color="error">รหัสสินค้าในรูป: {wrongImageWarning?.extractedItemNo}</Typography>
            <Typography fontWeight="bold" color="error">รหัสที่ควรเป็น: {wrongImageWarning?.expectedItemNo}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              URL ออเดอร์:{' '}
              <a
                href={wrongImageWarning?.expectedUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1d4ed8', textDecoration: 'underline', wordBreak: 'break-all' }}
              >
                {wrongImageWarning?.expectedUrl}
              </a>
            </Typography>
          </Box>

          <Typography variant="subtitle2" color="error" gutterBottom>รูปที่แนบไว้ (ผิด):</Typography>
          {wrongImageWarning?.wrongImagePreviewUrl ? (
            <img
              src={wrongImageWarning.wrongImagePreviewUrl}
              alt="Wrong attachment"
              style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 4, border: '2px solid #ef4444' }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">ไม่มีรูปตัวอย่าง</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 2 }}>
          <Button onClick={handleCloseWrongImageDialog} variant="contained" color="error" size="large" sx={{ minWidth: 200 }}>
            รับทราบ
          </Button>
        </DialogActions>
      </Dialog>


      <OrdererConfirmModal
        ordererMatch={pendingOrdererMatch}
        orderByOptions={orderByOptions}
        onConfirmed={() => { setPendingOrdererMatch(null); getPendingBeamBeforePurchases(); }}
      />

      <ConfirmDialog
        open={!!takeoverOrder}
        title="เคสนี้มีผู้อื่นกำลังดำเนินการ"
        message={`กำลังดำเนินการโดย ${takeoverOrder?.claimedByName || 'แอดมินท่านอื่น'}\nยืนยันจะดำเนินการต่อแทนหรือไม่?`}
        confirmText="ดำเนินการต่อ"
        onConfirm={() => { const o = takeoverOrder; setTakeoverOrder(null); openDialogNow(o, true); }}
        onClose={() => setTakeoverOrder(null)}
      />

      {/* Won-auction case: order-creation modal + takeover confirm */}
      <AuctionOrderModal
        open={!!selectedAuction}
        item={selectedAuction}
        shop={shopName}
        orderByOptions={orderByOptions}
        onClose={closeAuctionModal}
        onCreated={handleAuctionCreated}
      />
      <ConfirmDialog
        open={!!auctionTakeover}
        title="เคสนี้มีผู้อื่นกำลังดำเนินการ"
        message={`กำลังดำเนินการโดย ${auctionTakeover?.claimedByName || 'แอดมินท่านอื่น'}\nยืนยันจะรับช่วงต่อหรือไม่?`}
        confirmText="รับช่วงต่อ"
        onConfirm={() => { const a = auctionTakeover; setAuctionTakeover(null); openAuctionCase(a, true); }}
        onClose={() => setAuctionTakeover(null)}
      />
      <ConfirmDialog
        open={!!auctionToCancel}
        title="ลบรายการประมูลที่ชนะ"
        message={`ยืนยันลบรายการนี้ออกจากคิวหรือไม่? ใช้เมื่อซื้อไม่ได้จริง ๆ (เช่น ผู้ขายโดนแบน)\n${auctionToCancel?.itemUrl || ''}`}
        confirmText="ลบรายการ"
        confirmColor="error"
        onConfirm={handleConfirmCancelAuction}
        onClose={() => setAuctionToCancel(null)}
      />
      <Portal>
        <Backdrop open={isCheckingOrderer} sx={{ color: '#fff', zIndex: 99999, flexDirection: 'column', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>กำลังบันทึก / ตรวจสอบ orderer...</Typography>
        </Backdrop>
      </Portal>

      {/* Purchase Image Warning — postcode / name mismatch, shows AFTER successful submit */}
      {(() => {
        const hasPostcodeError = imageWarning && !imageWarning.isValid && !imageWarning.cannotValidate;
        const hasNameWarning   = !!imageWarning?.nameMismatchMessage;
        return (
          <Dialog open={imageWarning !== null} onClose={() => {}} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ backgroundColor: hasPostcodeError ? '#7f1d1d' : '#92400e', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '1.5rem' }}>{hasPostcodeError ? '🚨' : '⚠️'}</span>
                <strong>ตรวจพบข้อมูลในรูปยืนยันไม่ตรง</strong>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              {/* Tier 1: postcode wrong — strong red */}
              {hasPostcodeError && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef2f2', borderRadius: 1, border: '2px solid #dc2626' }}>
                  <pre style={{ color: '#991b1b', whiteSpace: 'pre-wrap', fontWeight: 'bold', margin: 0, fontSize: '0.85rem' }}>
                    {imageWarning.message}
                  </pre>
                </Box>
              )}
              {/* Tier 2: order number mismatch — softer amber */}
              {hasNameWarning && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #f59e0b' }}>
                  <Typography variant="body2" color="warning.dark">
                    ⚠️ {imageWarning.nameMismatchMessage}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button
                onClick={() => setImageWarning(null)}
                variant="contained"
                color={hasPostcodeError ? 'error' : 'warning'}
                size="large"
                sx={{ minWidth: 180 }}
              >
                รับทราบ
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

export default PendingBeamBeforePurchases;
